<script setup>
import BreadCrumbs from '@/components/breadcrumbs/BreadCrumbs.vue'
import ContentSubheaderPage from '@/components/content/ContentSubheaderPage.vue'
import CategoryFilters from '@/components/filters/CategoryFilters.vue'
import SearchField from '@/components/fields/SearchField.vue'
import ResourceCard from '@/components/resources/ResourceCard.vue'
import { onBeforeUpdate, onMounted, ref, watch } from 'vue'
import { useResourcesStore } from '@/store/resources'
import { storeToRefs } from 'pinia'
import * as helper from '@/store/helpers'
import debounce from 'lodash.debounce'

const resourcesStore = useResourcesStore()
const { formatResourcesTags } = storeToRefs(resourcesStore)

const isMounted = ref(false)

const categories = ref([])
const mobileCategories = ref([])
const selectedFilters = ref([])
const searchStr = ref('')
const isNewResourcesSorting = ref(true)

const resourcesCount = ref(0)
const resourceCards = ref([])

const pageNumber = ref(1) // Текущая страница
const paginationCount = ref(15) // Количество новостей на 1 странице
const maxPage = ref(0)
const pageElems = ref([]) // ref-ссылки на dom-элементы (номера страниц)

const getMaxPage = (resourcesCount) => {
    return Math.ceil(resourcesCount / paginationCount.value)
}

const changePage = (page) => {
    pageNumber.value = page

    for (let i = 0; i < pageElems.value.length; i++) {
        if (pageElems.value[i].classList.contains('toggle')) {
            pageElems.value[i].classList.remove('toggle')
            break
        }
    }

    if (pageElems.value.length > 1) {
        pageElems.value[page - 1].classList.add('toggle')
    }

    document
        .getElementById('all-resources-page')
        .scrollIntoView({ behavior: 'smooth' }) // Прокрутка страницы
}

const getResources = async (
    isEmpty = true,
    searchString = '',
    tagsId = [],
    isDesc = true
) => {
    if (isEmpty) {
        resourcesCount.value = await helper.api('GET', '/resources/count', {})
        await resourcesStore.getResources(
            0,
            paginationCount.value * pageNumber.value
        )
        resourceCards.value = resourcesStore.allResources
    } else {
        resourcesCount.value = await helper.api('GET', '/resources/count', {
            search_string: searchString,
            tags_id: tagsId
        })
        await resourcesStore.getResources(
            paginationCount.value * (pageNumber.value - 1),
            paginationCount.value * pageNumber.value,
            searchString,
            tagsId,
            isDesc
        )
        resourceCards.value = resourcesStore.allResources
    }
}

const changeSorting = () => {
    isNewResourcesSorting.value = !isNewResourcesSorting.value

    if (isNewResourcesSorting.value) {
        document.getElementById('resource-sort-arrow').style.transform =
            'rotate(0)'
        document.getElementById('resource-sort-arrow-mobile').style.transform =
            'rotate(0)'
    } else {
        document.getElementById('resource-sort-arrow').style.transform =
            'rotate(180deg)'
        document.getElementById('resource-sort-arrow-mobile').style.transform =
            'rotate(0)'
    }
}

const clearFilters = () => {
    searchStr.value = ''
    selectedFilters.value = []
}

watch(
    [searchStr, selectedFilters, isNewResourcesSorting],
    debounce(async () => {
        changePage(1)
        await getResources(
            false,
            searchStr.value,
            selectedFilters.value,
            isNewResourcesSorting.value
        )
        maxPage.value = getMaxPage(resourcesCount.value)
    }, 500)
)

watch(pageNumber, async () => {
    await getResources(
        false,
        searchStr.value,
        selectedFilters.value,
        isNewResourcesSorting.value
    )
})

onMounted(async () => {
    await resourcesStore.getResourcesTags(true)
    categories.value = formatResourcesTags.value

    for (const category of categories.value) {
        for (const subCategory of category.filters) {
            mobileCategories.value.push({
                id: subCategory.id,
                title: `${category.title}-${subCategory.title}`
            })
        }
    }

    await getResources()
    maxPage.value = getMaxPage(resourcesCount.value)

    isMounted.value = true
})

onBeforeUpdate(() => {
    pageElems.value = []
})
</script>

<template>
    <div
        id="all-resources-page"
        class="container"
    >
        <bread-crumbs />
        <content-subheader-page
            class="resource-page-content-subheader"
            text="РЕСУРСЫ"
        />
        <v-container
            :fluid="true"
            class="pa-0 resource-page-container"
        >
            <v-row class="ml-n2 mr-n2">
                <v-col
                    class="pl-2 pr-2 d-none d-lg-block"
                    cols="3"
                >
                    <div class="filter-card">
                        <h2
                            v-if="categories.length > 0"
                            class="filter-card-title"
                        >
                            Категории
                        </h2>
                        <category-filters
                            v-if="isMounted"
                            :conference-filters="categories"
                            :is-seminars="false"
                            @detect-select="
                                (selectedFiltersArg) => {
                                    selectedFilters = selectedFiltersArg
                                }
                            "
                        />
                    </div>
                </v-col>
                <v-col
                    class="pl-2 pr-2"
                    cols="12"
                    lg="9"
                >
                    <div class="sorting-field d-flex d-lg-none mb-6">
                        Сортировка:&nbsp;
                        <span @click="changeSorting">
                            по дате добавления
                            <img
                                id="resource-sort-arrow-mobile"
                                src="/svg/dropdown_arrow.svg"
                                alt="arrow"
                            />
                        </span>
                    </div>
                    <search-field v-model="searchStr" />
                    <v-select
                        v-model="selectedFilters"
                        :items="mobileCategories"
                        class="d-lg-none"
                        label="Все категории"
                        variant="outlined"
                        item-title="title"
                        item-value="id"
                        multiple
                    />
                    <v-btn
                        class="text-none inputs-button d-lg-none"
                        height="60"
                        variant="flat"
                        color="#3B66D3"
                        @click="clearFilters"
                    >
                        Сбросить фильтры
                    </v-btn>
                    <div class="sorting-field d-none d-lg-flex">
                        Сортировка:&nbsp;
                        <span @click="changeSorting">
                            по дате добавления
                            <img
                                id="resource-sort-arrow"
                                src="/svg/dropdown_arrow.svg"
                                alt="arrow"
                            />
                        </span>
                    </div>
                    <v-row class="ml-n2 mr-n2 mt-7">
                        <v-col
                            v-for="resourceCard in resourceCards"
                            :key="resourceCard.id"
                            class="pl-4 pr-4 pb-4 pt-4"
                            cols="12"
                            sm="6"
                            md="4"
                        >
                            <ResourceCard :resource-card-info="resourceCard" />
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </v-container>
        <v-row class="ml-n2 mr-n2">
            <v-col
                class="pl-2 pr-2 ml-auto"
                cols="12"
                lg="9"
            >
                <v-container
                    :fluid="true"
                    class="pa-0 pagination"
                    :class="{ 'd-none': maxPage < 2 }"
                >
                    <div
                        class="page-arrows"
                        @click="changePage(1)"
                    >
                        <svg
                            width="20"
                            height="15"
                            viewBox="0 0 20 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10 1.75152L8.48329 0.5L0 7.5L8.48329 14.5L10 13.2485L3.03342 7.5L10 1.75152Z"
                                fill="#0076DE"
                            />
                            <path
                                d="M20 1.75152L18.4833 0.5L10 7.5L18.4833 14.5L20 13.2485L13.0334 7.5L20 1.75152Z"
                                fill="#0076DE"
                            />
                        </svg>
                    </div>
                    <div
                        v-for="(page, i) in maxPage"
                        v-show="
                            (pageNumber < 5
                                ? page <= 5
                                : page < pageNumber + 3) &&
                            (pageNumber < 5 ? true : page > pageNumber - 3)
                        "
                        :key="page"
                        :ref="
                            (el) => {
                                if (el) pageElems[i] = el
                            }
                        "
                        class="page"
                        :class="{ toggle: i === 0 }"
                        @click="changePage(page)"
                    >
                        {{ page }}
                    </div>
                    <div
                        class="page-arrows"
                        @click="changePage(maxPage)"
                    >
                        <svg
                            width="20"
                            height="15"
                            viewBox="0 0 20 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10 13.2485L11.5167 14.5L20 7.5L11.5167 0.500001L10 1.75152L16.9666 7.5L10 13.2485Z"
                                fill="#0076DE"
                            />
                            <path
                                d="M0 13.2485L1.51671 14.5L10 7.5L1.51671 0.5L0 1.75152L6.96658 7.5L0 13.2485Z"
                                fill="#0076DE"
                            />
                        </svg>
                    </div>
                </v-container>
            </v-col>
        </v-row>
    </div>
</template>

<style scoped lang="scss">
.container {
    margin-top: 32px;
    margin-left: 104px;
    margin-right: 104px;

    @media screen and (max-width: 767px) {
        margin-left: 40px;
        margin-right: 40px;
        margin-top: 24px;
    }
}

.resource-page-content-subheader {
    text-align: left;
}

.resource-page-container {
    margin-top: 28px;
}

.filter-card {
    max-height: 1030px;
    border-radius: 5px;
    background: #fff;
    box-shadow: 1px 1px 50px 10px #2632380d;
    padding: 0 25px 20px 25px;
    display: flex;
    flex-direction: column;
    align-items: start;
}

.filter-card-title {
    font-size: 28px;
    font-weight: 500;
    line-height: 36px;
    color: #0635ac;
    margin-top: 20px;
}

.sorting-field {
    display: flex;
    justify-content: left;

    span {
        display: flex;
        align-items: center;
        color: #0635ac;
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
            color: #144cd8;
        }
    }
}

.inputs-button {
    width: 100%;
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
}

.pagination {
    display: flex;
    gap: 32px;
    align-items: center;
    justify-content: center;
    margin-top: 28px;

    .page-arrows {
        cursor: pointer;
    }

    .page {
        color: #0076de;
        font-size: 20px;
        line-height: 28px;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .toggle {
        text-decoration: underline;
    }
}
</style>
